import { entitySelectionPayload, selectedEntityIds, entityLabel } from '../../../shared/utils/entitySelection';
import { CALENDAR_EVENT_FORMS, hasCalendarEventField } from '../config/calendarEventTypes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import {
  createCalendarEvent,
  createCalendarEventNoteResult,
  deleteCalendarEvent as deleteCalendarEventMutation,
  deleteCalendarEventNoteResult,
  listCalendarEventNotesResult,
  listCalendarEventsResult,
  updateCalendarEvent as updateCalendarEventMutation,
} from '@/modules/calendar/services';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import {
  listExpensesResult,
  listPaymentsWithClientIdsResult,
} from '@/modules/accounting/services';
import { listSuppliersResult } from '@/modules/suppliers/services/suppliers.service';
import {
  listAssignmentsResult,
  listCompletedDealsResult,
  listInProgressDealsResult,
  listProposalsResult,
} from '@/modules/deals/services';
import { listStaffResult } from '@/modules/staff/services/staff.service';
import { buildDerivedCalendarEvents, markManualCalendarEvent } from '../utils/calendar.catalog';
import { CALENDAR_EVENT_TYPE_OPTIONS, INITIAL_NEW_EVENT } from '@/modules/calendar/utils';
import {
  buildEventDateSet,
  filterEvents,
  getResetEventForm,
  sortEventsByDateTime,
} from '@/modules/calendar/utils';

const eventTypes = [...CALENDAR_EVENT_TYPE_OPTIONS];
const PRESET_DURATIONS = new Set(['30m', '1h', '2h', '4h']);

function getResultData(result) {
  return result?.ok ? result.data : [];
}

function getEventFormFromSelectedEvent(event) {
  if (!event) return getResetEventForm();

  const isCustomDuration = !PRESET_DURATIONS.has(event.duration);

  return {
    title: event.title || '',
    description: event.description || '',
    amount: event.amount ?? '',
    date: event.date || '',
    endDate: hasCalendarEventField(event.type, 'endDate') ? event.endDate || event.date || '' : '',
    time: event.time || '',
    duration: hasCalendarEventField(event.type, 'duration') && isCustomDuration ? 'custom' : event.duration || '1h',
    durationCustom: isCustomDuration ? event.duration || '' : '',
    type: event.type || 'meeting',
    attendees: Array.isArray(event.attendees) ? [...event.attendees] : [],
    location: event.location || '',
    priority: event.priority || 'media',
    ...entitySelectionPayload(event),
    clientId: event.clientId ?? '',
    supplierId: event.supplierId ?? '',
    customFields: { ...(event.customFields || {}) },
  };
}

export function useCalendarData() {
  const { currentUser } = useSession();
  const [events, setEvents] = useState([]);
  const [derivedEvents, setDerivedEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [eventsModuleDisabled, setEventsModuleDisabled] = useState(false);
  const [eventsUnavailable, setEventsUnavailable] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventsAuthRequired, setEventsAuthRequired] = useState(false);
  const [eventsTenantDatabaseBlocked, setEventsTenantDatabaseBlocked] = useState(false);
  const [calendarCatalogStatus, setCalendarCatalogStatus] = useState('idle');
  const [date, setDate] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventNotesById, setEventNotesById] = useState({});
  const [eventNotesError, setEventNotesError] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [eventDialogMode, setEventDialogMode] = useState('create');
  const [activeFilters, setActiveFilters] = useState([]);
  const [showNext15, setShowNext15] = useState(false);
  const [newEvent, setNewEvent] = useState(INITIAL_NEW_EVENT);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitMutexRef = useRef(false);
  const submitIdempotencyKeyRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadEvents() {
      const [
        eventsResult,
        staffResult,
        clientsResult,
        suppliersResult,
        assignmentsResult,
        paymentsResult,
        expensesResult,
        proposalsResult,
        inProgressDealsResult,
        completedDealsResult,
      ] = await Promise.all([
        listCalendarEventsResult(),
        listStaffResult(),
        listClientsResult(),
        listSuppliersResult(),
        listAssignmentsResult(),
        listPaymentsWithClientIdsResult(),
        listExpensesResult(),
        listProposalsResult(),
        listInProgressDealsResult(),
        listCompletedDealsResult(),
      ]);

      if (!isMounted) {
        return;
      }

      setEvents(eventsResult.ok ? sortEventsByDateTime(eventsResult.data.map(markManualCalendarEvent)) : []);
      setDerivedEvents(
        sortEventsByDateTime(
          buildDerivedCalendarEvents({
            assignments: getResultData(assignmentsResult),
            payments: getResultData(paymentsResult),
            expenses: getResultData(expensesResult),
            proposals: getResultData(proposalsResult),
            inProgressDeals: getResultData(inProgressDealsResult),
            completedDeals: getResultData(completedDealsResult),
          })
        )
      );
      setStaff(staffResult.ok ? staffResult.data : []);
      setClients(clientsResult.ok ? clientsResult.data : []);
      setSuppliers(suppliersResult.ok ? suppliersResult.data : []);
      setIsLoadingEvents(false);
      const moduleDisabled = eventsResult.error === 'CALENDAR_MODULE_DISABLED';
      const tenantDatabaseBlocked =
        eventsResult.error === 'CALENDAR_TENANT_DATABASE_NOT_READY' ||
        eventsResult.error === 'CALENDAR_TENANT_DATABASE_METADATA_MISSING' ||
        eventsResult.error === 'CALENDAR_TENANT_DATABASE_UNAVAILABLE';
      setEventsModuleDisabled(moduleDisabled);
      setEventsUnavailable(
        !eventsResult.ok &&
          eventsResult.error !== 'CALENDAR_AUTH_REQUIRED' &&
          !moduleDisabled &&
          !tenantDatabaseBlocked
      );
      setEventsAuthRequired(eventsResult.error === 'CALENDAR_AUTH_REQUIRED');
      setEventsTenantDatabaseBlocked(tenantDatabaseBlocked);
      setEventsError(eventsResult.ok ? '' : eventsResult.message || eventsResult.error || '');
      setCalendarCatalogStatus(
        eventsResult.ok
          ? eventsResult.data.length > 0
            ? 'available'
            : 'empty'
          : moduleDisabled
            ? 'module_disabled'
          : tenantDatabaseBlocked
            ? 'tenant_database_unavailable'
          : eventsResult.error === 'CALENDAR_AUTH_REQUIRED'
            ? 'auth_required'
            : 'unavailable'
      );
    }

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, []);

  const allEvents = useMemo(() => {
    const clientsById = new Map(clients.map((client) => [String(client.id), client]));
    const suppliersById = new Map(suppliers.map((supplier) => [String(supplier.id), supplier]));
    return sortEventsByDateTime([...events, ...derivedEvents].map((event) => {
      const client = event.clientId ? clientsById.get(String(event.clientId)) : null;
      const supplier = event.supplierId ? suppliersById.get(String(event.supplierId)) : null;
      const clientName = selectedEntityIds(event, 'client').length > 1 ? selectedEntityIds(event, 'client').map((id) => entityLabel(clientsById.get(id))).filter(Boolean).join('; ') : event.clientName || client?.company || client?.businessName || client?.name || '';
      const supplierName = selectedEntityIds(event, 'supplier').length > 1 ? selectedEntityIds(event, 'supplier').map((id) => entityLabel(suppliersById.get(id))).filter(Boolean).join('; ') : event.supplierName || supplier?.displayName || supplier?.legalName || '';
      return {
        ...event,
        clientName,
        supplierName,
      };
    }));
  }, [clients, events, derivedEvents, suppliers]);

  const eventDateSet = useMemo(() => buildEventDateSet(allEvents), [allEvents]);

  const filteredEvents = useMemo(() => {
    return filterEvents({
      events: allEvents,
      selectedDate: date || null,
      activeFilters,
      showNext15,
    });
  }, [allEvents, date, activeFilters, showNext15]);

  const upcomingEvents = useMemo(() => {
    return sortEventsByDateTime(filteredEvents);
  }, [filteredEvents]);

  const totalUpcomingCount = useMemo(() => {
    return allEvents.length;
  }, [allEvents]);

  function handleDateSelect(value) {
    if (!value) {
      setDate('');
      setSelectedDateRange({ startDate: '', endDate: '' });
      return;
    }

    setShowNext15(false);
    setSelectedDateRange((current) => {
      if (!current.startDate || current.endDate) {
        setDate(value);
        return { startDate: value, endDate: '' };
      }

      const [startDate, endDate] = [current.startDate, value].sort();
      setDate('');
      return { startDate, endDate };
    });
  }

  function clearSelection() {
    setDate('');
    setSelectedDateRange({ startDate: '', endDate: '' });
    setShowNext15(false);
    setSelectedEvent(null);
  }

  function toggleNext15() {
    setShowNext15((prev) => {
      const next = !prev;
      if (next) setDate('');
      return next;
    });
  }

  function toggleFilter(value) {
    setActiveFilters((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  }

  function clearFilters() {
    setActiveFilters([]);
  }

  function toggleAttendee(attendeeId) {
    setNewEvent((prev) => {
      const exists = prev.attendees.some((id) => String(id) === String(attendeeId));

      return {
        ...prev,
        attendees: exists
          ? prev.attendees.filter((id) => String(id) !== String(attendeeId))
          : [...prev.attendees, attendeeId],
      };
    });
  }

  function updateNewEventField(field, value) {
    setNewEvent((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'type' && !hasCalendarEventField(value, 'endDate') ? { endDate: '' } : {}),
    }));

    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  }

  function closeEventDialog() {
    setNewEvent(getResetEventForm());
    setFormErrors({});
    setIsCreateDialogOpen(false);
    setIsTypeDialogOpen(false);
    submitIdempotencyKeyRef.current = null;
    setEventDialogMode('create');
  }

  function openCreateDialog() {
    setEventDialogMode('create');
    const resetForm = getResetEventForm();
    setNewEvent({
      ...resetForm,
      date: selectedDateRange.startDate || date || resetForm.date,
      endDate: selectedDateRange.endDate || selectedDateRange.startDate || date || resetForm.endDate || '',
    });
    setFormErrors({});
    setIsCreateDialogOpen(false);
    setIsTypeDialogOpen(true);
    submitIdempotencyKeyRef.current = null;
  }

  function selectEventType(type) {
    if (!CALENDAR_EVENT_FORMS[type]) return;
    setNewEvent((current) => ({
      ...current, type,
      endDate: hasCalendarEventField(type, 'endDate') ? current.endDate || selectedDateRange.endDate || current.date : '',
    }));
    setFormErrors({});
    submitIdempotencyKeyRef.current = null;
    setIsTypeDialogOpen(false);
    setIsCreateDialogOpen(true);
  }

  function backToEventTypes() {
    if (isSubmitting) return;
    setIsCreateDialogOpen(false);
    setIsTypeDialogOpen(true);
  }

  function openEditSelectedEvent() {
    return openEditEvent(selectedEvent);
  }

  function openEditEvent(event) {
    if (!event) {
      return {
        ok: false,
        data: null,
        error: 'CALENDAR_EVENT_NOT_SELECTED',
        message: 'Nessun evento selezionato.',
      };
    }

    if (event.isReadOnly) {
      return {
        ok: false,
        data: null,
        error: 'CALENDAR_EVENT_READ_ONLY',
        message: 'Le scadenze derivate dagli altri moduli sono in sola lettura.',
      };
    }

    setSelectedEvent(event);
    setIsTypeDialogOpen(false);
    setEventDialogMode('edit');
    setNewEvent(getEventFormFromSelectedEvent(event));
    setFormErrors({});
    setIsCreateDialogOpen(true);

    return {
      ok: true,
      data: { eventId: event.id },
      error: null,
      message: 'Modifica evento aperta.',
    };
  }

  async function handleSubmitEvent(e) {
    e.preventDefault();

    if (submitMutexRef.current) return;
    submitMutexRef.current = true;
    setIsSubmitting(true);

    if (eventDialogMode !== 'edit' && !submitIdempotencyKeyRef.current) {
      submitIdempotencyKeyRef.current = globalThis.crypto?.randomUUID?.()
        || `calendar-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    let result;
    try {
      result = eventDialogMode === 'edit'
        ? await updateCalendarEventMutation(selectedEvent?.id, newEvent)
        : await createCalendarEvent(newEvent, submitIdempotencyKeyRef.current);
    } finally {
      submitMutexRef.current = false;
      setIsSubmitting(false);
    }

    if (!result.ok) {
      setFormErrors(result.data?.errors || { submit: result.message || 'Impossibile salvare l’evento.' });
      return;
    }

    submitIdempotencyKeyRef.current = null;

    if (eventDialogMode === 'edit') {
      setEvents((current) =>
        sortEventsByDateTime(
          current.map((item) =>
            item.id === result.data.event.id ? markManualCalendarEvent(result.data.event) : item
          )
        )
      );
      setSelectedEvent(markManualCalendarEvent(result.data.event));
    } else {
      setEvents((current) =>
        sortEventsByDateTime([...current, markManualCalendarEvent(result.data.event)])
      );
    }

    closeEventDialog();
  }

  async function deleteSelectedEvent() {
    return deleteEvent(selectedEvent);
  }

  async function deleteEvent(event) {
    if (!event) {
      return {
        ok: false,
        data: null,
        error: 'CALENDAR_EVENT_NOT_SELECTED',
        message: 'Nessun evento selezionato.',
      };
    }

    if (event.isReadOnly) {
      return {
        ok: false,
        data: null,
        error: 'CALENDAR_EVENT_READ_ONLY',
        message: 'Le scadenze derivate dagli altri moduli sono in sola lettura.',
      };
    }

    setSelectedEvent(event);
    const result = await deleteCalendarEventMutation(event.id);
    if (!result.ok) return result;

    setEvents((current) =>
      current.filter((item) => item.id !== result.data.deletedEventId)
    );
    setSelectedEvent((current) =>
      current?.id === result.data.deletedEventId ? null : current
    );

    return result;
  }

  async function loadEventNotes(eventId) {
    if (!eventId || String(eventId).includes(':')) return;
    const result = await listCalendarEventNotesResult(eventId);
    if (!result.ok) {
      setEventNotesError(result.message || 'Note evento non disponibili.');
      return;
    }

    setEventNotesById((current) => ({
      ...current,
      [eventId]: result.data,
    }));
    setEventNotesError('');
  }

  async function addEventNote(eventId, payload) {
    const result = await createCalendarEventNoteResult(eventId, payload);
    if (!result.ok) {
      setEventNotesError(result.message || 'Nota evento non salvata.');
      return result;
    }

    setEventNotesById((current) => ({
      ...current,
      [eventId]: [...(current[eventId] || []), result.data.comment],
    }));
    setEventNotesError('');
    return result;
  }

  async function deleteEventNote(eventId, noteId) {
    const result = await deleteCalendarEventNoteResult(eventId, noteId);
    if (!result.ok) {
      setEventNotesError(result.message || 'Nota evento non eliminata.');
      return result;
    }

    setEventNotesById((current) => ({
      ...current,
      [eventId]: (current[eventId] || []).filter((note) => String(note.id) !== String(noteId)),
    }));
    return result;
  }

  return {
    events: allEvents,
    manualEvents: events,
    derivedEvents,
    eventDateSet,
    eventTypes,
    staff,
    clients,
    suppliers,
    currentUser,
    isLoadingEvents,
    eventsModuleDisabled,
    eventsUnavailable,
    eventsError,
    eventsAuthRequired,
    eventsTenantDatabaseBlocked,
    calendarCatalogStatus,
    date,
    selectedDateRange,
    selectedEvent,
    setSelectedEvent,
    isCreateDialogOpen,
    isTypeDialogOpen,
    selectEventType,
    backToEventTypes,
    eventDialogMode,
    activeFilters,
    setActiveFilters,
    showNext15,
    newEvent,
    setNewEvent,
    formErrors,
    isSubmitting,
    eventNotesById,
    eventNotesError,
    upcomingEvents,
    totalUpcomingCount,
    handleDateSelect,
    clearSelection,
    toggleNext15,
    toggleFilter,
    clearFilters,
    toggleAttendee,
    updateNewEventField,
    openCreateDialog,
    openEditSelectedEvent,
    openEditEvent,
    handleSubmitEvent,
    loadEventNotes,
    addEventNote,
    deleteEventNote,
    deleteSelectedEvent,
    deleteEvent,
    closeEventDialog,
  };
}
